/**
 * 全局配置管理
 */

import type { SeacloudClient } from './client.js';
import type { PollingOptions } from './types.js';
import { SeacloudClient as Client } from './client.js';
import { getBaseUrl } from './config.js';

// 全局配置状态
interface GlobalConfig {
  client: SeacloudClient | null;
  defaultPollingOptions: PollingOptions;
  customHeaders: Record<string, string>;
}

const globalConfig: GlobalConfig = {
  client: null,
  defaultPollingOptions: {
    intervalMs: 3000,    // 3秒轮询间隔
    maxAttempts: 100,    // 最多尝试100次 (约5分钟)
  },
  customHeaders: {},
};

/**
 * 初始化配置选项
 */
export interface InitOptions {
  apiKey?: string;
  baseUrl?: string;
  intervalMs?: number;      // 轮询间隔（毫秒）
  maxAttempts?: number;     // 最大尝试次数
  timeout?: number;
  xProject?: string;        // X-Project header 值，用于 Agent API 访问标识，默认为 'SeaVerse'
}

/**
 * 初始化 SDK（可选）
 *
 * 从 v0.9.2 版本开始，此函数是可选的。
 * 如果不调用此函数，SDK 会在每次 API 调用时自动使用最新的环境变量/localStorage token。
 *
 * 何时调用此函数：
 * 1. 需要自定义 polling 选项（intervalMs, maxAttempts）
 * 2. 需要固定使用某个特定的 apiKey/baseUrl（不希望每次都重新读取）
 *
 * 在 iframe 环境中，SDK 会自动从父页面获取 Host 并判断环境（开发/正式），
 * 然后根据环境自动设置合适的 baseUrl。
 *
 * @example
 * // 不调用 initSeacloud - 每次都使用最新 token
 * const result = await llmChatCompletions({...});
 *
 * @example
 * // 调用 initSeacloud - 使用固定配置
 * initSeacloud({ intervalMs: 5000, maxAttempts: 200 });
 * const result = await llmChatCompletions({...});
 *
 * @example
 * // 在 iframe 中调用 - 自动判断环境
 * await initSeacloud(); // 会自动从父页面获取 host 并设置 baseUrl
 *
 * @param apiKeyOrConfig API 密钥字符串或完整配置对象
 * @param options 可选配置（仅在第一个参数为字符串时使用）
 */
export async function initSeacloud(
  apiKeyOrConfig?: string | InitOptions,
  options?: {
    baseUrl?: string;
    intervalMs?: number;
    maxAttempts?: number;
    timeout?: number;
  }
): Promise<SeacloudClient> {
  let apiKey: string | undefined;
  let config: InitOptions;

  // 判断第一个参数的类型
  if (typeof apiKeyOrConfig === 'string') {
    // 方式 1: initSeacloud('api-key', { ... })
    apiKey = apiKeyOrConfig;
    config = options || {};
  } else if (apiKeyOrConfig && typeof apiKeyOrConfig === 'object') {
    // 方式 2: initSeacloud({ apiKey: '...', baseUrl: '...', ... })
    config = apiKeyOrConfig;
    apiKey = config.apiKey;
  } else {
    // 方式 3: initSeacloud() - 无参数，完全依赖自动检测
    config = {};
    apiKey = undefined;
  }

  // 使用统一的 getBaseUrl 函数获取 baseUrl
  if (!config.baseUrl) {
    config.baseUrl = await getBaseUrl();
  }

  // 创建客户端（允许空 API key，让后端处理认证错误）
  globalConfig.client = await Client.create({
    apiKey: apiKey || '',
    baseUrl: config.baseUrl,
    timeout: config.timeout,
    xProject: config.xProject,
  });

  // 设置默认轮询选项
  if (config.intervalMs !== undefined) {
    globalConfig.defaultPollingOptions.intervalMs = config.intervalMs;
  }
  if (config.maxAttempts !== undefined) {
    globalConfig.defaultPollingOptions.maxAttempts = config.maxAttempts;
  }

  return globalConfig.client;
}

/**
 * 获取客户端实例
 *
 * 如果用户调用过 initSeacloud()，返回缓存的客户端实例
 * 否则，创建新的临时客户端实例（使用最新的环境变量/localStorage token）
 *
 * @returns SeacloudClient 实例
 */
export async function getClient(): Promise<SeacloudClient> {
  // 如果用户显式调用了 initSeacloud()，使用缓存的 client
  if (globalConfig.client) {
    return globalConfig.client;
  }

  // 否则，每次都动态创建新 client（使用最新的 token/env）
  return await Client.create({
    // 不传 apiKey - 让 createConfig() 自动从环境变量/localStorage 读取
    // 不传 baseUrl - 让 createConfig() 自动从环境变量读取或使用默认值
    // 不传 timeout - 使用默认 30000ms
  });
}

/**
 * 获取默认轮询选项
 */
export function getDefaultPollingOptions(): PollingOptions {
  return { ...globalConfig.defaultPollingOptions };
}

/**
 * 更新默认轮询选项
 */
export function setDefaultPollingOptions(options: Partial<PollingOptions>) {
  Object.assign(globalConfig.defaultPollingOptions, options);
}

/**
 * 设置全局自定义 HTTP headers
 *
 * 这些 headers 会在所有 API 请求中自动添加到请求头中。
 * 可用于设置请求追踪 ID、客户端标识等自定义 header。
 *
 * @example
 * ```typescript
 * import { setCustomHeaders } from 'seacloud-sdk';
 *
 * // 设置自定义 headers
 * setCustomHeaders({
 *   'X-Request-ID': 'req-123',
 *   'X-Client-Version': '1.0.0',
 *   'X-Custom-Header': 'custom-value'
 * });
 *
 * // 后续所有 API 调用都会带上这些 headers
 * const result = await klingV1({ prompt: 'test' });
 * ```
 *
 * @param headers 自定义 headers 对象
 */
export function setCustomHeaders(headers: Record<string, string>) {
  globalConfig.customHeaders = { ...headers };
}

/**
 * 获取当前设置的全局自定义 headers
 *
 * @returns 当前的自定义 headers 对象
 */
export function getCustomHeaders(): Record<string, string> {
  return { ...globalConfig.customHeaders };
}

/**
 * 清除所有全局自定义 headers
 *
 * @example
 * ```typescript
 * import { clearCustomHeaders } from 'seacloud-sdk';
 *
 * clearCustomHeaders();
 * ```
 */
export function clearCustomHeaders() {
  globalConfig.customHeaders = {};
}

/**
 * 重置配置（主要用于测试）
 */
export function resetConfig() {
  globalConfig.client = null;
  globalConfig.defaultPollingOptions = {
    intervalMs: 3000,
    maxAttempts: 100,
  };
  globalConfig.customHeaders = {};
}
